import type { TransportData } from '@/domainLogic/index.ts';
import { getAdultsChildrenCount } from '@/utils/peopleUtils.ts';
import type { SearchParams } from '@/server/searchParams.ts';
import { getISODateFromFlightDate } from '@/app/components/hotel-detail/common/dates';

export const SEARCH_DEFAULT_FLIGHT = 'SEARCH_DEFAULT_FLIGHT';
export const SEARCH_DEFAULT_FLIGHT_VERSION = '2.1';

enum Baggage {
  NOT_INCLUDED_BAGGAGE = 'NOT_INCLUDED_BAGGAGE',
  INCLUDED_BAGGAGE = 'INCLUDED_BAGGAGE',
  HALF_INCLUDED_BAGGAGE = 'HALF_INCLUDED_BAGGAGE',
}

export type SearchDefaultFlightPayload = {
  searchId: string;
  vcSearchId: string;
  tripId: string;
  outboundIataOrigin: string;
  outboundIataArrival: string;
  outboundStops: number;
  outboundAirline: string;
  outboundFlightId: string;
  outboundOriginLocalTime: string;
  outboundArrivalLocalTime: string;
  outboundDurationInMin: number;
  inboundIataOrigin: string;
  inboundIataArrival: string;
  inboundStops: number;
  inboundAirline: string;
  inboundFlightId: string;
  inboundOriginLocalTime: string;
  inboundArrivalLocalTime: string;
  inboundDurationInMin: number;
  baggage: string;
  flightsCost: number;
  flightType: string;
  outboundAirlineProvidedLuggageQty: number;
  inboundAirlineProvidedLuggageQty: number;
  luggageAddedQty: number;
};

const getBaggage = (transportData: TransportData): Baggage => {
  if (transportData.outbound?.baggage?.hold && transportData.inbound?.baggage?.hold) return Baggage.INCLUDED_BAGGAGE;
  if (transportData.outbound?.baggage?.hold || transportData.inbound?.baggage?.hold)
    return Baggage.HALF_INCLUDED_BAGGAGE;
  return Baggage.NOT_INCLUDED_BAGGAGE;
};

const getBaggageByDetails = (transport: TransportsType): Baggage => {
  if (transport.outbound?.includeBaggage && transport.inbound?.includeBaggage) return Baggage.INCLUDED_BAGGAGE;
  if (transport.outbound?.includeBaggage || transport.inbound?.includeBaggage) return Baggage.HALF_INCLUDED_BAGGAGE;
  return Baggage.NOT_INCLUDED_BAGGAGE;
};

export type SearchDefaultFlightParams = {
  searchId: string;
  vcSearchId: string;
  transportData: TransportData;
  searchParams: SearchParams;
};
export type SearchDefaultFlightParamsByDetails = {
  searchId: string;
  vcSearchId: string;
  transport: TransportsType;
  passeengersWithLuggage: number;
};

export const createSearchDefaultFlightPayloadByDetails = ({
  searchId,
  vcSearchId,
  transport,
  passeengersWithLuggage,
}: SearchDefaultFlightParamsByDetails): SearchDefaultFlightPayload => {
  const outboundSegments = transport.outbound?.segments;
  const firstOutboundSegment = outboundSegments[0];
  const lastOutboundSegment =
    outboundSegments && outboundSegments.length > 0
      ? outboundSegments[outboundSegments.length - 1]
      : firstOutboundSegment;
  const outboundIataOrigin = firstOutboundSegment?.departureHub?.id;
  const outboundIataArrival = lastOutboundSegment?.arrivalHub?.id;
  const outboundStops = transport.outbound.segments.length - 1;
  const outboundAirline = firstOutboundSegment?.company?.code;
  const outboundFlightId = transport.outbound?.segments
    .map((segment) => `${segment.company?.code}${segment.transportNumber}`)
    .join('#');
  const outboundOriginLocalTime = getISODateFromFlightDate(firstOutboundSegment?.departureDate);
  const outboundArrivalLocalTime = getISODateFromFlightDate(lastOutboundSegment?.arrivalDate);
  const outboundDurationInMin = transport.outbound?.timeIntervalInMinutes || 0;
  const firstInboundSegment = transport.inbound?.segments[0];
  const lastInboundSegment = transport.inbound?.segments[transport.inbound.segments.length - 1];
  const inboundIataOrigin = firstInboundSegment?.departureHub?.id;
  const inboundIataArrival = lastInboundSegment?.arrivalHub?.id;
  const inboundStops = transport.inbound.segments.length - 1;
  const inboundAirline = firstInboundSegment?.company?.code;
  const inboundFlightId = transport.inbound?.segments
    .map((segment) => `${segment.company?.code}${segment.transportNumber}`)
    .join('#');
  const inboundOriginLocalTime = getISODateFromFlightDate(firstInboundSegment?.departureDate);
  const inboundArrivalLocalTime = getISODateFromFlightDate(lastInboundSegment?.arrivalDate);
  const inboundDurationInMin = transport.inbound?.timeIntervalInMinutes || 0;
  const { id: tripId } = transport;
  const baggage = getBaggageByDetails(transport);

  const luggageAddedQty = transport.numberOfBaggagePiecesIncluded || 0;
  const outboundAirlineProvidedLuggageQty = luggageAddedQty
    ? 0
    : Number(!!transport.outbound?.includeBaggage) * passeengersWithLuggage;
  const inboundAirlineProvidedLuggageQty = luggageAddedQty
    ? 0
    : Number(!!transport.inbound?.includeBaggage) * passeengersWithLuggage;

  return {
    searchId: searchId || '',
    vcSearchId: vcSearchId || '',
    tripId,
    outboundIataOrigin,
    outboundIataArrival,
    outboundStops,
    outboundAirline,
    outboundFlightId,
    outboundOriginLocalTime,
    outboundArrivalLocalTime,
    outboundDurationInMin,
    inboundIataOrigin,
    inboundIataArrival,
    inboundStops,
    inboundAirline,
    inboundFlightId,
    inboundOriginLocalTime,
    inboundArrivalLocalTime,
    inboundDurationInMin,
    baggage,
    flightsCost: -1 /* Not available in this context */,
    flightType: '' /* Not available in this context */,
    outboundAirlineProvidedLuggageQty,
    inboundAirlineProvidedLuggageQty,
    luggageAddedQty,
  };
};

export const createSearchDefaultFlightPayload = ({
  searchId,
  vcSearchId,
  transportData,
  searchParams,
}: SearchDefaultFlightParams): SearchDefaultFlightPayload => {
  const firstOutboundSegment = transportData.outbound?.segments[0];
  const lastOutboundSegment = transportData.outbound?.segments[transportData.outbound.segments.length - 1];
  const outboundIataOrigin = firstOutboundSegment?.departure?.hub?.id;
  const outboundIataArrival = lastOutboundSegment?.arrival?.hub?.id;
  const outboundStops = transportData.outbound.segments.length - 1;
  const outboundAirline = firstOutboundSegment?.transport?.companyCode;
  const outboundFlightId = transportData.outbound?.segments.map((segment) => segment.transport.operativeId).join('#');
  const outboundOriginLocalTime = firstOutboundSegment?.departure?.datetime;
  const outboundArrivalLocalTime = lastOutboundSegment?.arrival?.datetime;
  const outboundDurationInMin = transportData.outbound?.durationInMinutes || 0;
  const firstInboundSegment = transportData.inbound?.segments[0];
  const lastInboundSegment = transportData.inbound?.segments[transportData.inbound.segments.length - 1];
  const inboundIataOrigin = firstInboundSegment?.departure?.hub?.id;
  const inboundIataArrival = lastInboundSegment?.arrival?.hub?.id;
  const inboundStops = transportData.inbound.segments.length - 1;
  const inboundAirline = firstInboundSegment?.transport?.companyCode;
  const inboundFlightId = transportData.inbound?.segments.map((segment) => segment.transport.operativeId).join('#');
  const inboundOriginLocalTime = firstInboundSegment?.departure?.datetime;
  const inboundArrivalLocalTime = lastInboundSegment?.arrival?.datetime;
  const inboundDurationInMin = transportData.inbound?.durationInMinutes || 0;
  const { tripId } = transportData;
  const baggage = getBaggage(transportData);
  const { flightCost: flightsCost, flightType } = transportData;

  const adultsChildrenCount = getAdultsChildrenCount(searchParams);
  const luggageAddedQty = transportData.includedTotalCheckedBags || 0;
  const outboundAirlineProvidedLuggageQty = luggageAddedQty
    ? 0
    : Number(!!transportData.outbound?.baggage?.hold) * adultsChildrenCount;
  const inboundAirlineProvidedLuggageQty = luggageAddedQty
    ? 0
    : Number(!!transportData.inbound?.baggage?.hold) * adultsChildrenCount;

  return {
    searchId,
    vcSearchId,
    tripId,
    outboundIataOrigin,
    outboundIataArrival,
    outboundStops,
    outboundAirline,
    outboundFlightId,
    outboundOriginLocalTime,
    outboundArrivalLocalTime,
    outboundDurationInMin,
    inboundIataOrigin,
    inboundIataArrival,
    inboundStops,
    inboundAirline,
    inboundFlightId,
    inboundOriginLocalTime,
    inboundArrivalLocalTime,
    inboundDurationInMin,
    baggage,
    flightsCost,
    flightType,
    outboundAirlineProvidedLuggageQty,
    inboundAirlineProvidedLuggageQty,
    luggageAddedQty,
  };
};
